import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { RepeatRegularlyView, RepeatRegularlyViewProps } from './RepeatRegularly.view';
import { UseFormReturn } from 'react-hook-form';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { TimezoneSetting } from '../../../../models/graphql';
import { PostEditorRecurringIntervalType } from '../../../../models/post-editor';
import { OnUpdatePostOnTimeFunction } from '../PostOnDate/PostOnDate.view';
import { useDidMount } from '../../../../hooks/useDidMount';
import { dateRoundTo } from '../../../../helpers/map-timezone';
import { FormElementAction, FormElementValueType } from '../../../../models/basic-objects';
import { utcToZonedTime } from 'date-fns-tz';
import { TimezoneMap } from 'constants/timezones';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { hasSeverity } from '../../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { getErrorMessages } from '../../validators/helpers/getErrorMessages';

export type RepeatRegularlyControllerProps = {
  // extend your RepeatRegularlyViewProps
  formInstance: RepeatRegularlyViewProps['formInstance'];
  groupName: RepeatRegularlyViewProps['groupName'];
};

export const RepeatRegularlyController = (props: RepeatRegularlyControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { formInstance, groupName } = props;
  const formInstanceRef = useRef<UseFormReturn>(formInstance);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const { state, dispatch } = useContext(PostEditorContext);

  const { selectedWorkspace } = useSelectedOrganization();
  const wsTimezone = selectedWorkspace?.timezone ?? TimezoneSetting.UTC;

  // TODO on each render array is a new object! it triggers 2 renders because of that
  const scheduledDate =
    typeof customizeIndex === 'number'
      ? state.postGroups[postGroupIndex].posts[customizeIndex].scheduling.recurring.date
      : state.postGroups[postGroupIndex].groupPostAttributes.scheduling.recurring.date;
  const timeZone =
    (typeof customizeIndex === 'number'
      ? state.postGroups[postGroupIndex].posts[customizeIndex].scheduling.scheduledTimezone
      : state.postGroups[postGroupIndex].groupPostAttributes.scheduling.scheduledTimezone) ?? wsTimezone;
  const recurringDataType =
    typeof customizeIndex === 'number'
      ? state.postGroups[postGroupIndex].posts[customizeIndex].scheduling.recurringType
      : state.postGroups[postGroupIndex].groupPostAttributes.scheduling.recurringType;
  const recurringDataValue =
    typeof customizeIndex === 'number'
      ? state.postGroups[postGroupIndex].posts[customizeIndex].scheduling.recurringValue
      : state.postGroups[postGroupIndex].groupPostAttributes.scheduling.recurringValue;
  const recurringNotInitializedRef = useRef<boolean>(
    recurringDataType === null && recurringDataValue === null && scheduledDate === null
  );
  const customizedProfileId =
    state.postGroups[postGroupIndex].customizeSchedulePerProfile && typeof customizeIndex === 'number'
      ? state.postGroups[postGroupIndex].posts[customizeIndex].customizedProfileId
      : null;
  const { errors: postGroupErrors } = state.postGroups[postGroupIndex];
  const localTime = new Date().toISOString();
  const newDate = utcToZonedTime(localTime, TimezoneMap[timeZone]);
  const roundedNext = useRef<Date>(dateRoundTo(newDate));

  const onUpdate: OnUpdatePostOnTimeFunction = useCallback(
    (date: Date, complete?: (keepNew?: boolean) => void) => {
      complete && complete();
      dispatch({
        type: 'recurringUpdateDate',
        payload: {
          postGroupIndex,
          date: date, // The date is already with the correct timezone
          customizeIndex
        }
      });
    },
    [postGroupIndex, customizeIndex, dispatch]
  );

  const isPostEditorRecurringIntervalTypeGuard = (
    value: FormElementValueType
  ): value is PostEditorRecurringIntervalType => {
    if (typeof value !== 'string') throw new Error('value is not of type string!');
    return value in PostEditorRecurringIntervalType;
  };

  const updateIntervalType: FormElementAction = useCallback(
    (value, complete) => {
      if (!isPostEditorRecurringIntervalTypeGuard(value)) throw new Error('value is not a proper enum');
      complete();
      const enumValue: PostEditorRecurringIntervalType = value;
      dispatch({
        type: 'recurringSetInterval',
        payload: {
          type: enumValue,
          postGroupIndex,
          customizeIndex
        }
      });
    },
    [postGroupIndex, customizeIndex, dispatch]
  );

  const isTimezoneSettingGuard = (value: FormElementValueType): value is TimezoneSetting => {
    if (typeof value !== 'string') throw new Error('value is not of type string!');
    return value in TimezoneSetting;
  };

  const selectAction: FormElementAction = useCallback(
    (value, complete) => {
      if (!isTimezoneSettingGuard(value)) throw new Error('value is not a proper enum');
      complete();
      const scheduledTimezone: TimezoneSetting = value;
      dispatch({
        type: 'changeTimeZone',
        payload: {
          scheduledTimezone,
          postGroupIndex,
          customizeIndex
        }
      });
    },
    [postGroupIndex, customizeIndex, dispatch]
  );

  const updateRecurringValue: FormElementAction<unknown> = useCallback(
    (value, complete) => {
      if (typeof value !== 'number' && typeof value !== 'string') {
        complete(false);
        throw new Error('value is not of type number nor string!');
      }
      complete();
      if (value) {
        dispatch({
          type: 'recurringSetValue',
          payload: {
            postGroupIndex,
            value: +value,
            customizeIndex
          }
        });
      }
    },
    [postGroupIndex, customizeIndex, dispatch]
  );

  const postOnDateErrorMessage = getErrorMessages(postGroupErrors, customizedProfileId, null, ['checkDateAndTime']);
  const hasPostOnDateError = hasSeverity(postGroupErrors, ErrorMessageSeverity.error, customizedProfileId, null, [
    'checkDateAndTime'
  ]);
  const postOnDateError = hasPostOnDateError ? postOnDateErrorMessage.join('\n') : undefined;
  const hasPostOnDateWarning = hasSeverity(postGroupErrors, ErrorMessageSeverity.warning, customizedProfileId, null, [
    'checkDateAndTime'
  ]);
  const postOnDateWarning = hasPostOnDateWarning ? postOnDateErrorMessage.join('\n') : undefined;

  const recurringIntervalValueMessage = getErrorMessages(postGroupErrors, customizedProfileId, null, [
    'checkRecurringInterval'
  ]);
  const hasRecurringIntervalValueError = hasSeverity(
    postGroupErrors,
    ErrorMessageSeverity.error,
    customizedProfileId,
    null,
    ['checkRecurringInterval']
  );
  const recurringIntervalValueError = hasRecurringIntervalValueError
    ? recurringIntervalValueMessage.join('\n')
    : undefined;

  const hasRecurringIntervalValueWarning = hasSeverity(
    postGroupErrors,
    ErrorMessageSeverity.warning,
    customizedProfileId,
    null,
    ['checkRecurringInterval']
  );
  const recurringIntervalValueWarning = hasRecurringIntervalValueWarning
    ? recurringIntervalValueMessage.join('\n')
    : undefined;

  useEffect(() => {
    if (
      (recurringDataType === null || recurringDataValue === null || scheduledDate === null) &&
      recurringNotInitializedRef
    ) {
      recurringNotInitializedRef.current = true;
      dispatch({
        type: 'recurringInitialize',
        payload: {
          postGroupIndex,
          customizeIndex,
          value: recurringDataValue === null ? 4 : undefined,
          date: scheduledDate === null ? roundedNext.current : undefined,
          type: recurringDataType === null ? PostEditorRecurringIntervalType.weeks : undefined
        }
      });
    }
  }, [customizeIndex, postGroupIndex, recurringDataType, recurringDataValue, scheduledDate, dispatch]);

  useDidMount(() => () => {
    formInstanceRef.current.unregister([`${groupName}.intervalValue`, `${groupName}.intervalType`]);
  });

  if (scheduledDate && typeof recurringDataValue === 'number' && recurringDataType) {
    const viewProps: RepeatRegularlyViewProps = {
      ...props,
      onUpdate,
      selectAction,
      updateIntervalType,
      updateRecurringValue,
      timeZone,
      recurringDataType,
      recurringDataValue,
      scheduledDate,
      postOnDateError,
      postOnDateWarning,
      recurringIntervalValueError,
      recurringIntervalValueWarning,
      expandTimezoneSelector: timeZone !== wsTimezone
    };
    return <RepeatRegularlyView {...viewProps} />;
  }
  return <></>;
};
